import React, { useCallback, useEffect } from 'react';

import Input from 'ui-kit/Input/Input';
import Stack from 'ui-kit/Stack';
import { CalculatorOutlined, CloseOutlined } from '@ant-design/icons';
import { Divider, Form, message, Popover } from 'antd';

import { Typography, Link, IconButton, Button, Checkbox } from 'ui-kit';
import { saveStoreOwnerProfitPercentage } from 'api/store_sdk';
import _ from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import * as yup from 'yup';

const validSchema = yup.object({
  owner_profit_percentage: yup
    .number()
    .min(0, 'Minimum value is 0')
    .max(100, 'Maximum value is 100')
    .required(),
});

export interface IPayoutCalculatorPopover {
  isOpen: boolean;
  enabledFeature: boolean;
  storeName: string;
  storeId: number | null;
  initialOwnerProfitPercentage: number;
  previewMode: boolean;
  onOpen: () => void;
  onClose: () => void;
  onCalculate: () => void;
}

const PayoutCalculatorPopover = ({
  isOpen,
  enabledFeature,
  storeName,
  storeId,
  initialOwnerProfitPercentage,
  previewMode = false,
  onOpen,
  onClose,
  onCalculate,
}: IPayoutCalculatorPopover) => {
  const { handleSubmit, control, watch, reset } = useForm({
    resolver: yupResolver(validSchema),
  });

  const currentValue = watch('owner_profit_percentage');

  useEffect(() => {
    if (isOpen) {
      reset({ owner_profit_percentage: initialOwnerProfitPercentage, save_to_all_stores: false });
    } else {
      reset();
    }
  }, [isOpen, initialOwnerProfitPercentage]);

  const handleCalculate = useCallback(
    (data) => {
      saveStoreOwnerProfitPercentage({
        storeId,
        ownerProfitPercentage: data.owner_profit_percentage,
        saveToAllStores: data.save_to_all_stores,
      })
        .then(() => onCalculate())
        .catch((error) => {
          Object.values(error).forEach((fieldErrors: Array<string>) => {
            _.map(fieldErrors, (fieldError: string) => {
              message.error(fieldError);
            });
          });
        });
    },
    [storeId, onCalculate],
  );

  return (
    <Popover
      visible={isOpen}
      content={
        isOpen && (
          <div>
            {enabledFeature ? (
              <Form
                layout="vertical"
                initialValues={{
                  owner_profit_percentage: initialOwnerProfitPercentage,
                  save_to_all_stores: false,
                }}
                style={{ marginBottom: '16px' }}
              >
                <Stack gap="8px" alignItems="flex-start">
                  <span style={{ flex: 1 }}>
                    <Controller
                      control={control}
                      name="owner_profit_percentage"
                      render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                        <Form.Item
                          name={name}
                          validateStatus={error ? 'error' : ''}
                          help={error ? error?.message : null}
                        >
                          <Input
                            addonAfter="%"
                            defaultValue={value}
                            type="number"
                            onChange={onChange}
                            min={0}
                            max={100}
                            disabled={previewMode}
                          />
                        </Form.Item>
                      )}
                    />
                  </span>{' '}
                  <span style={{ flex: 1, marginTop: '8px' }}>
                    / {(100 - currentValue).toFixed(2)} %
                  </span>
                </Stack>
                <Stack gap="8px" alignItems="flex-start" style={{ marginBottom: '8px' }}>
                  <Controller
                    control={control}
                    name="save_to_all_stores"
                    render={({ field: { name, value, onChange } }) => (
                      <Form.Item name={name}>
                        <Checkbox checked={value} onChange={onChange} disabled={previewMode}>
                          Save this percentage to all stores
                        </Checkbox>
                      </Form.Item>
                    )}
                  />
                </Stack>
                <Typography variant="caption" as="p">
                  * This calculation is for the store {storeName} only.
                </Typography>
                <Typography variant="caption" as="p">
                  For reporting only, does not pay out to both parties.
                </Typography>
                <Divider />
                <Stack justifyContent="flex-end" gap="8px">
                  <Button onClick={onClose}>Cancel</Button>
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={(data) => handleSubmit(handleCalculate)(data)}
                    disabled={previewMode}
                  >
                    Calculate
                  </Button>
                </Stack>
              </Form>
            ) : (
              <p className="upgrade-txt">
                * <Link href={`/pricing/`}>Upgrade to Pro Plus</Link> to calculate the split between
                Party A and Party B
              </p>
            )}
          </div>
        )
      }
      title={
        <Stack justifyContent="space-between" alignItems="center">
          <Typography variant="body1">Calculate split between Party A and Party B</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </Stack>
      }
      trigger="click"
    >
      {' '}
      <IconButton onClick={onOpen}>
        <CalculatorOutlined />
      </IconButton>
    </Popover>
  );
};

export default PayoutCalculatorPopover;
